import {
  Animator,
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';
import { SpineView } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { PetEntity } from 'src/game/logic/EntityManager';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import { waitForItPromise } from 'src/lib/utils';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { rescueLevelThemeCrocodile } from '../themes/rescueLevel.theme.crocodile';

const HEADER_WIDTH = 720;
const HEADER_HEIGHT = 400;
const SPINE_X = HEADER_WIDTH / 2;
const SPINE_Y = HEADER_HEIGHT / 2;
const CROC_BACKGROUND_SCALE = DOWNSCALE_FACTOR * 0.65;
const CROC_FOREGROUND_SCALE = DOWNSCALE_FACTOR * 0.95;
const CROC_POSITIONS = {
  approach: {
    background: { x: 417, y: 175 },
    foreground: { x: 450, y: 383 },
  },
  aggressive: {
    background: { x: 295, y: 193 },
    foreground: { x: 350, y: 333 },
  },
} as const;
const PET_PLACEHOLDER = 'placeholder_buru';
const BOAT_POSITION_BONE = 'Rowboat_position';
const PET_PLACEHOLDER_SETUP_SCALE = 0.6;
const PET_BOARDS_BOAT_DELAY_MS = 1667;

function hideCrocLilypad(croc: SpineView) {
  // The current export shows the lily pad in its setup pose, while croc-only
  // animations never key this slot. Pet-scene skeletons keep it attached.
  croc.skeleton.findSlot('lilypad')?.setAttachment(null);
}

// The guide/storyboard shortens these to lilypad_idle, lilypad_attack, and
// lilypad_escape. The supplied Spine export includes "_buru" because the same
// placeholder moves Buru between the lily pad, boat, and dock.
const LILYPAD_ANIMATIONS = {
  idle: 'lilypad_buru_idle',
  attack: {
    background: 'lilypad_buru_attack_bg',
    foreground: 'lilypad_buru_attack_fg',
  },
  escape: {
    background: 'lilypad_buru_escape_bg',
    foreground: 'lilypad_buru_escape_fg',
  },
  onDock: 'lilypad_buru_ondock',
  empty: 'lilypad_empty',
} as const;

export class HeaderCrocodile extends View {
  private readonly petSceneBackground: SpineView;
  private readonly petSceneForeground: SpineView;
  private readonly crocForeground: SpineView;
  private readonly crocBackground: SpineView;
  public readonly pet: PetEntity;
  public tenSecsLeft = false;
  private crocApproachAnimations: Animator[] = [];
  private turnCrocsAfterBoatPasses = false;
  private trackPetFromForeground = false;

  constructor(opts: ViewOpts) {
    super({
      ...opts,
      width: HEADER_WIDTH,
      height: HEADER_HEIGHT,
      clip: true,
    });

    new ImageScaleView({
      superview: this,
      image: rescueLevelThemeCrocodile.getHeaderBackground(),
      width: downscale(1140),
      height: downscale(710),
      x: SPINE_X,
      y: SPINE_Y,
      centerOnOrigin: true,
      zIndex: -3,
    });

    this.crocBackground = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeCrocodile.getHeaderSpine(),
      scale: CROC_BACKGROUND_SCALE,
      ...CROC_POSITIONS.approach.background,
      zIndex: -2,
      visible: false,
    });
    hideCrocLilypad(this.crocBackground);

    const petScene = new View({
      superview: this,
      scale: DOWNSCALE_FACTOR,
      x: SPINE_X,
      y: SPINE_Y,
      zIndex: -1,
    });

    this.petSceneBackground = new SpineView({
      superview: petScene,
      spinePath: rescueLevelThemeCrocodile.getHeaderSpine(),
      x: 0,
      y: 0,
      zIndex: -1,
    });

    this.pet = createUIPet('sad');
    this.pet.updateOpts({
      superview: petScene,
      x: 0,
      y: 0,
      anchorY: 300,
      offsetY: -300,
      zIndex: 0,
    });

    this.petSceneForeground = new SpineView({
      superview: petScene,
      spinePath: rescueLevelThemeCrocodile.getHeaderSpine(),
      x: 0,
      y: 0,
      zIndex: 1,
      visible: false,
    });

    this.crocForeground = new SpineView({
      superview: this,
      spinePath: rescueLevelThemeCrocodile.getHeaderSpine(),
      scale: CROC_FOREGROUND_SCALE,
      ...CROC_POSITIONS.approach.foreground,
      zIndex: 2,
    });
    hideCrocLilypad(this.crocForeground);

    this.petSceneBackground.state.addListener({
      event: (_entry, event) => {
        if (event.data.name === 'fire') this.pet.hide();
      },
    });
    this.setTick(this.updateSceneWithSpine.bind(this));

    void this.playIdle();
  }

  public async playIdle() {
    this.stopCrocApproach();
    this.setCrocPositions('approach');
    this.tenSecsLeft = false;
    this.turnCrocsAfterBoatPasses = false;
    this.trackPetFromForeground = false;
    this.petSceneForeground.hide();
    this.setPetAnimation('sad');
    this.pet.show();

    void playSpineAnimation(this.petSceneBackground, {
      animation: LILYPAD_ANIMATIONS.idle,
      loop: true,
    });

    await Promise.all([
      this.playCrocAppear(this.crocForeground),
      (async () => {
        // The guide asks for the two emerge/idle cycles to be staggered.
        await waitForItPromise(250);
        if (this.tenSecsLeft) return;
        await this.playCrocAppear(this.crocBackground);
      })(),
    ]);
  }

  public playPuzzleStart(timeUntilTenSecsMs: number) {
    this.stopCrocApproach();
    this.setCrocPositions('approach');

    this.crocApproachAnimations = [
      animate(this.crocForeground).now(
        CROC_POSITIONS.aggressive.foreground,
        timeUntilTenSecsMs - 1000,
        animate.linear,
      ),
      animate(this.crocBackground).now(
        CROC_POSITIONS.aggressive.background,
        timeUntilTenSecsMs - 1000,
        animate.linear,
      ),
    ];
  }

  public async playTenSecsLeft() {
    this.stopCrocApproach();
    this.setCrocPositions('aggressive');
    this.tenSecsLeft = true;
    this.crocBackground.show();
    this.setPetAnimation('very_sad');

    void playSpineAnimation(this.crocForeground, {
      animation: 'croc_idle_angry1',
      loop: true,
    });
    void playSpineAnimation(this.crocBackground, {
      animation: 'croc_idle_angry2',
      loop: true,
    });
  }

  public async playRescue() {
    this.stopCrocApproach();
    this.crocBackground.show();

    this.playCrocReaction('croc_idle_questionmark');

    // The escape animation spends its first 1.667s bringing the boat to Buru.
    // Keep Buru distressed until boarding the boat.
    void this.playBoardingReaction();

    await this.playLayeredPetSceneAnimation(LILYPAD_ANIMATIONS.escape);
    void playSpineAnimation(this.petSceneBackground, {
      animation: LILYPAD_ANIMATIONS.onDock,
      loop: true,
    });
    this.trackPetFromForeground = false;
  }

  public async playFail() {
    this.stopCrocApproach();
    this.crocBackground.show();

    await this.playFailSequence();
    await waitForItPromise(2500);
  }

  public async playQuit() {
    this.stopCrocApproach();
    this.crocBackground.show();

    void this.playFailSequence();
    await waitForItPromise(1500);
  }

  private async playFailSequence() {
    await Promise.all([
      playSpineAnimation(this.crocForeground, {
        animation: 'croc_dive',
      }),
      (async () => {
        await playSpineAnimation(this.crocBackground, {
          animation: 'croc_idle_to_laugh',
        });
        void playSpineAnimation(this.crocBackground, {
          animation: 'croc_laugh',
          loop: true,
        });
      })(),
    ]);

    await this.playLayeredPetSceneAnimation(LILYPAD_ANIMATIONS.attack);
    void playSpineAnimation(this.petSceneBackground, {
      animation: LILYPAD_ANIMATIONS.empty,
      loop: true,
    });
    this.trackPetFromForeground = false;
  }

  private async playBoardingReaction() {
    await waitForItPromise(PET_BOARDS_BOAT_DELAY_MS);
    this.setPetAnimation('normal');
    this.turnCrocsAfterBoatPasses = true;
  }

  private playCrocReaction(animation: string) {
    void playSpineAnimation(this.crocForeground, { animation, loop: true });
    void playSpineAnimation(this.crocBackground, { animation, loop: true });
  }

  private async playCrocAppear(croc: SpineView) {
    croc.show();
    await playSpineAnimation(croc, {
      animation: 'croc_emerge',
    });
    void playSpineAnimation(croc, {
      animation: 'croc_idle',
      loop: true,
    });
  }

  private async playLayeredPetSceneAnimation(animation: {
    readonly background: string;
    readonly foreground: string;
  }) {
    // The foreground escape owns the pet path that meets the on-dock pose.
    this.trackPetFromForeground = true;
    this.petSceneForeground.show();
    await Promise.all([
      playSpineAnimation(this.petSceneBackground, {
        animation: animation.background,
      }),
      playSpineAnimation(this.petSceneForeground, {
        animation: animation.foreground,
      }),
    ]);
    this.petSceneForeground.hide();
  }

  private updateSceneWithSpine() {
    const petPositionSpine = this.trackPetFromForeground
      ? this.petSceneForeground
      : this.petSceneBackground;
    const bone = petPositionSpine.skeleton.findBone(PET_PLACEHOLDER);
    if (bone) {
      this.pet.updateOpts({
        x: bone.worldX,
        y: bone.worldY,
        r: (bone.getWorldRotationX() * Math.PI) / 180,
        scaleX: bone.getWorldScaleX() / PET_PLACEHOLDER_SETUP_SCALE,
        scaleY: bone.getWorldScaleY() / PET_PLACEHOLDER_SETUP_SCALE,
      });
    }

    if (!this.turnCrocsAfterBoatPasses) return;

    const boat = this.petSceneBackground.skeleton.findBone(BOAT_POSITION_BONE);
    if (!boat) return;

    const boatX = SPINE_X + boat.worldX * DOWNSCALE_FACTOR;
    const rightmostCrocX = Math.max(
      this.crocForeground.style.x,
      this.crocBackground.style.x,
    );
    if (boatX <= rightmostCrocX) return;

    this.turnCrocsAfterBoatPasses = false;
    this.playCrocReaction('croc_idle_questionmark_turned');
  }

  private setCrocPositions(position: keyof typeof CROC_POSITIONS) {
    this.crocBackground.updateOpts(CROC_POSITIONS[position].background);
    this.crocForeground.updateOpts(CROC_POSITIONS[position].foreground);
  }

  private stopCrocApproach() {
    this.crocApproachAnimations.forEach((animation) => animation.clear());
    this.crocApproachAnimations = [];
  }

  private setPetAnimation(state: 'normal' | 'sad' | 'very_sad') {
    const species = getPet().species ?? 'dog';
    this.pet.setAnimation(petAnimationNames[species][state].idle);
  }
}
